import React, { useMemo, useCallback, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { ByMedia } from '@this/shared/ui/other/by_media';
import HamburgerButton from '@this/shared/header/hamburger_button';
import { useAITTheme, type AITTheme } from '@this/shared/ui/theme';
import SidebarMenu from '../trip_ai_messages/components/SidebarMenu';
import HistoryListPanel from '../trip_ai_messages/components/HistoryListPanel';
import CalendarStarterCard from '../trip_ai_messages/components/CalendarStarterCard';
import UserChatRuleSettingsModal from '../trip_ai_messages/components/UserChatRuleSettingsModal';
import { createDraftTripWithMessage } from '../trip_ai_messages/api/messages';
import type { AiMessagePayload } from '../trip_ai_messages/api/messages';
import { useCalendarConnection } from '../trip_ai_messages/hooks/useCalendarConnection';
import NewChatWelcome from './NewChatWelcome';
import NewChatInput from './NewChatInput';

const TripAiMessagesNewPage = () => {
  const { theme } = useAITTheme();
  const history = useHistory();
  const styles = useMemo(() => getStyles(theme), [theme]);
  const [showHistoryList, setShowHistoryList] = useState(false);
  const [showUserChatRuleSettings, setShowUserChatRuleSettings] = useState(false);
  const [showStarterCard, setShowStarterCard] = useState(false);
  const [isSidebarDrawerOpen, setIsSidebarDrawerOpen] = useState(false);
  const { isCalendarConnected } = useCalendarConnection();

  const handleSend = useCallback(
    async (text: string) => {
      const result = await createDraftTripWithMessage(text, []);
      history.replace({
        pathname: `/trips/${result.tripId}/ai_messages`,
        state: { initialMessage: text }
      });
    },
    [history]
  );

  const handleCalendarStarterCardOpen = useCallback(() => {
    setShowStarterCard(true);
  }, []);

  const handleSendPayload = useCallback(
    async (payload: AiMessagePayload) => {
      const result = await createDraftTripWithMessage(payload.text, []);
      history.replace({
        pathname: `/trips/${result.tripId}/ai_messages`,
        state: { initialMessage: payload.text }
      });
    },
    [history]
  );

  const handleShowHistoryList = () => {
    setIsSidebarDrawerOpen(false);
    setShowHistoryList(true);
  };

  const handleHideHistoryList = () => {
    setShowHistoryList(false);
  };

  const sidebarMenu = (
    <SidebarMenu
      currentTripId="new"
      onShowAllHistory={handleShowHistoryList}
      onOpenUserChatRuleSettings={() => setShowUserChatRuleSettings(true)}
      onNavigate={() => setIsSidebarDrawerOpen(false)}
    />
  );

  const newChatContent = (
    <>
      <NewChatWelcome />
      {showStarterCard && <CalendarStarterCard onSubmit={handleSendPayload} />}
      <NewChatInput
        onSend={handleSend}
        isCalendarConnected={isCalendarConnected}
        onCalendarStarterCardOpen={handleCalendarStarterCardOpen}
      />
    </>
  );

  return (
    <div style={styles.root}>
      <div style={styles.body}>
        <ByMedia>
          {matches =>
            matches.sm || matches.md ? (
              <>
                {showHistoryList ? (
                  <HistoryListPanel onBack={handleHideHistoryList} />
                ) : (
                  <main style={styles.mainAreaDrawer}>
                    <div style={styles.mobileHeader}>
                      <div style={styles.hamburgerWrap}>
                        <HamburgerButton active={false} onClick={() => setIsSidebarDrawerOpen(true)} />
                      </div>
                    </div>
                    <div style={styles.mainContent}>{newChatContent}</div>
                  </main>
                )}
                {isSidebarDrawerOpen && (
                  <div
                    role="button"
                    tabIndex={0}
                    aria-label="閉じる"
                    style={styles.drawerOverlay}
                    onClick={() => setIsSidebarDrawerOpen(false)}
                    onKeyDown={e => e.key === 'Escape' && setIsSidebarDrawerOpen(false)}
                  />
                )}
                <div
                  style={{
                    ...styles.leftDrawer,
                    transform: isSidebarDrawerOpen ? 'translateX(0)' : 'translateX(-100%)'
                  }}
                >
                  <div style={styles.drawerCloseRow}>
                    <button
                      type="button"
                      style={styles.drawerCloseButton}
                      onClick={() => setIsSidebarDrawerOpen(false)}
                      aria-label="閉じる"
                    >
                      ✕
                    </button>
                  </div>
                  <div style={styles.drawerScroll}>{sidebarMenu}</div>
                </div>
              </>
            ) : (
              <>
                {sidebarMenu}
                {showHistoryList ? (
                  <HistoryListPanel onBack={handleHideHistoryList} />
                ) : (
                  <main style={styles.mainArea}>{newChatContent}</main>
                )}
              </>
            )
          }
        </ByMedia>
        <UserChatRuleSettingsModal
          open={showUserChatRuleSettings}
          onClose={() => setShowUserChatRuleSettings(false)}
        />
      </div>
    </div>
  );
};

const getStyles = (theme: AITTheme) => ({
  root: {
    width: '100%',
    height: 'calc(100vh - 80px)',
    display: 'flex' as const,
    flexDirection: 'column' as const,
    minHeight: 0
  },
  body: {
    display: 'flex' as const,
    flex: 1,
    position: 'relative' as const,
    backgroundColor: theme.whiteColor,
    border: `1px solid ${theme.grayBorderColor}`,
    borderRadius: '8px',
    overflow: 'hidden',
    minHeight: 0
  },
  mainArea: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column' as const,
    alignItems: 'center',
    justifyContent: 'center',
    gap: '32px',
    padding: '24px',
    minHeight: 0
  },
  mainAreaDrawer: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column' as const,
    minHeight: 0
  },
  mobileHeader: {
    display: 'flex',
    alignItems: 'center' as const,
    padding: '8px',
    flexShrink: 0
  },
  hamburgerWrap: {
    display: 'flex',
    alignItems: 'center' as const,
    flexShrink: 0
  },
  mainContent: {
    flex: 1,
    minHeight: 0,
    overflowY: 'auto' as const,
    display: 'flex',
    flexDirection: 'column' as const,
    alignItems: 'center',
    justifyContent: 'center',
    gap: '24px',
    padding: '16px',
    width: '100%'
  },
  drawerOverlay: {
    position: 'fixed' as const,
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    zIndex: 1000
  },
  leftDrawer: {
    position: 'fixed' as const,
    top: 0,
    left: 0,
    width: 'min(80vw, 280px)',
    height: '100vh',
    backgroundColor: theme.contentBgColor,
    boxShadow: '2px 0 8px rgba(0, 0, 0, 0.15)',
    zIndex: 1001,
    transition: 'transform 0.3s ease-in-out',
    display: 'flex' as const,
    flexDirection: 'column' as const,
    overflow: 'hidden' as const
  },
  drawerCloseRow: {
    display: 'flex' as const,
    justifyContent: 'flex-end' as const,
    padding: '8px',
    flexShrink: 0
  },
  drawerCloseButton: {
    background: 'none',
    border: 'none',
    fontSize: '20px',
    lineHeight: 1,
    color: theme.textColor,
    cursor: 'pointer',
    padding: '4px 8px'
  },
  drawerScroll: {
    flex: 1,
    minHeight: 0,
    overflowY: 'auto' as const,
    display: 'flex' as const,
    flexDirection: 'column' as const
  }
});

export default TripAiMessagesNewPage;
